import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mail, CalendarPlus, Loader2, Eye, RotateCcw, MessageSquare, Users } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useAdmin } from "../../pages/admin/AdminApp";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import CardCollapse from "./CardCollapse";
import ImageCropUpload from "./ImageCropUpload";

/* Editable email templates with a live preview iframe.
 * - Invite  : calendar invitation sent via /admin/calendar/<eid>/share
 * - Reply   : auto-reply sent to the person who submitted a contact form
 *
 * Both templates are stored under settings.communication so the admin can
 * tweak the wording without touching code. Variables available in text:
 *   {{name}}, {{host}}, {{site}}
 */

const PLACEHOLDERS = (
  <div className="text-[11px] text-stone-500 flex flex-wrap gap-x-3 gap-y-1 mt-1">
    <span><code className="bg-stone-100 px-1 rounded">{"{{name}}"}</code> gönderenin adı</span>
    <span><code className="bg-stone-100 px-1 rounded">{"{{host}}"}</code> sizin adınız</span>
    <span><code className="bg-stone-100 px-1 rounded">{"{{site}}"}</code> site adı</span>
  </div>
);

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-stone-600">{label}</span>
      {children}
      {hint ? <span className="block text-[11px] text-stone-400">{hint}</span> : null}
    </label>
  );
}

function PreviewFrame({ html, loading, subject }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.srcdoc = html || "<html><body style='font-family:sans-serif;padding:40px;color:#777;text-align:center'>Önizleme hazırlanıyor…</body></html>";
  }, [html]);
  return (
    <div className="rounded-xl border border-stone-200 bg-stone-50 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 text-xs text-stone-500">
        <span className="flex items-center gap-1.5"><Eye size={13} />Canlı önizleme</span>
        {loading ? <Loader2 size={13} className="animate-spin" /> : null}
      </div>
      {subject ? (
        <div className="rounded-lg border bg-white px-3 py-2 text-xs text-stone-700">
          <span className="text-[10px] uppercase tracking-wider text-stone-400 mr-2">Konu</span>
          <span className="font-medium">{subject}</span>
        </div>
      ) : null}
      <iframe ref={ref} title="E-posta önizleme" className="w-full h-[520px] rounded-lg border bg-white" sandbox="" />
    </div>
  );
}

function useTemplate(section, defaults, kind) {
  const { settings, saveSection } = useAdmin();
  const stored = settings.communication?.[section] || {};
  const [tpl, setTpl] = useState(() => ({ ...defaults, ...stored }));
  const [preview, setPreview] = useState({ html: "", subject: "", loading: true });
  const [busy, setBusy] = useState(false);
  // Where to fetch live previews. Calendar + reply templates share one
  // endpoint; the appointment-request template has its own.
  const previewUrl = kind === "appointment"
    ? "/admin/appointments/preview"
    : "/admin/calendar/templates/preview";
  const previewPayload = kind === "appointment"
    ? { name: "Ahmet Yılmaz", title: "Proje tanışma toplantısı",
        reason: "<p>Konuşmak istediğim konuyu kısaca anlatan bir paragraf. <strong>Zengin metin</strong> destekli.</p>",
        duration_minutes: 30, template: tpl }
    : { kind, template: tpl };

  // Keep in sync when server state changes (e.g. after save).
  useEffect(() => {
    setTpl((prev) => ({ ...defaults, ...stored, ...prev }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(stored)]);

  // Debounced preview refresh.
  useEffect(() => {
    let alive = true;
    setPreview((p) => ({ ...p, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(previewUrl, previewPayload);
        if (alive) setPreview({ html: data.html, subject: data.subject, loading: false });
      } catch (e) {
        if (alive) setPreview({ html: `<html><body style='padding:30px;color:#b91c1c;font-family:sans-serif'>Önizleme hatası: ${errMsg(e)}</body></html>`, subject: "", loading: false });
      }
    }, 450);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tpl)]);

  const save = async () => {
    setBusy(true);
    try {
      await saveSection("communication", { [section]: tpl });
      toast.success("Şablon kaydedildi");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const reset = () => setTpl({ ...defaults });

  return { tpl, setTpl, preview, save, busy, reset };
}

const INVITE_DEFAULTS = {
  greeting: "Merhaba {{name}},",
  intro: "Aşağıdaki etkinliği takviminize eklemeniz için gönderdim. Düğmelerden tek tıkla ekleyebilirsiniz.",
  outro: "Görüşmek üzere.",
  signature: "{{host}}",
  sms_text: "Merhaba {{name}}, randevumuzu takvime eklemek için: {{link}}",
};
const REPLY_DEFAULTS = {
  enabled: false,
  subject: "Mesajınızı aldım — {{site}}",
  greeting: "Merhaba {{name}},",
  body: "Mesajınız bana ulaştı. En kısa sürede size geri dönüş yapacağım.\n\nİlginiz için teşekkür ederim.",
  signature: "Sevgiler,\n{{host}}",
};

function InviteCard() {
  const { tpl, setTpl, preview, save, busy, reset } = useTemplate("invite_template", INVITE_DEFAULTS, "invite");
  const chip = <span className="text-[11px] text-stone-400">Takvim davetiyesi</span>;
  return (
    <CardCollapse
      testid="comm-template-invite"
      icon={<CalendarPlus size={16} />}
      title="Takvim davetiye şablonu"
      description="Takvim üzerinden gönderdiğiniz davetiyelerde yer alacak metin ve iletişim fotoğrafınız. Önizleme sağdadır."
      chip={chip}
    >
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <Field label="Selamlama" hint="E-posta en üstte görünür. Değişkenler aşağıda.">
            <Input value={tpl.greeting || ""} onChange={(e) => setTpl({ ...tpl, greeting: e.target.value })} data-testid="invite-greeting" />
          </Field>
          <Field label="Açıklama">
            <Textarea rows={3} value={tpl.intro || ""} onChange={(e) => setTpl({ ...tpl, intro: e.target.value })} data-testid="invite-intro" />
          </Field>
          <Field label="Kapanış cümlesi">
            <Textarea rows={2} value={tpl.outro || ""} onChange={(e) => setTpl({ ...tpl, outro: e.target.value })} data-testid="invite-outro" />
          </Field>
          <Field label="İmza">
            <Input value={tpl.signature || ""} onChange={(e) => setTpl({ ...tpl, signature: e.target.value })} data-testid="invite-signature" />
          </Field>
          {PLACEHOLDERS}

          <div className="pt-3 mt-3 border-t border-stone-200">
            <ImageCropUpload
              label="Davetiye fotoğrafı"
              hint="E-posta başlığında ad/unvanınızın yanında çıkar. Yuvarlak (avatar) veya dikdörtgen (logo) olarak kırpabilirsiniz. Boş bırakılırsa iletişim ayarlarındaki fotoğraf kullanılır."
              value={tpl.photo || ""}
              onChange={(url) => setTpl((p) => ({ ...p, photo: url }))}
              shape={tpl.photo_shape || "round"}
              onShapeChange={(sh) => setTpl((p) => ({ ...p, photo_shape: sh }))}
              alt="Davetiye şablonu fotoğrafı"
              testid="invite-photo"
            />
          </div>

          <div className="pt-3 mt-3 border-t border-stone-200 space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-stone-700">
              <MessageSquare size={13} className="text-rose-600" />
              SMS daveti (opsiyonel)
            </div>
            <p className="text-[11px] text-stone-500 leading-snug">
              Davetiyeyi SMS ile göndereceğiniz zaman kullanılacak kısa metin. <code className="bg-stone-100 px-1 rounded">{"{{link}}"}</code> yerine telefonun takvim uygulamasında etkinliği açacak kısa bir bağlantı yerleşir — alıcı tıkladığında doğrudan takvime ekle sayfası gelir.
            </p>
            <Textarea
              rows={3}
              maxLength={150}
              value={tpl.sms_text || ""}
              onChange={(e) => setTpl({ ...tpl, sms_text: e.target.value.slice(0, 150) })}
              placeholder="Merhaba {{name}}, randevumuzu takvime eklemek için: {{link}}"
              data-testid="invite-sms-text"
            />
            <div className="flex items-center justify-between text-[11px] text-stone-500">
              <span>Değişkenler: <code className="bg-stone-100 px-1 rounded">{"{{name}}"}</code>, <code className="bg-stone-100 px-1 rounded">{"{{link}}"}</code>, <code className="bg-stone-100 px-1 rounded">{"{{host}}"}</code></span>
              <span className={(tpl.sms_text || "").length > 140 ? "text-amber-700 font-semibold" : ""}>{(tpl.sms_text || "").length}/150</span>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button type="button" onClick={save} disabled={busy} data-testid="invite-save">
              {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}Kaydet
            </Button>
            <Button type="button" variant="ghost" onClick={reset} data-testid="invite-reset">
              <RotateCcw size={13} className="mr-1" />Varsayılana döndür
            </Button>
          </div>
        </div>
        <PreviewFrame html={preview.html} loading={preview.loading} subject={preview.subject} />
      </div>
    </CardCollapse>
  );
}

function ReplyCard() {
  const { tpl, setTpl, preview, save, busy, reset } = useTemplate("contact_reply_template", REPLY_DEFAULTS, "contact_reply");
  const chip = (
    <label className="flex items-center gap-2 text-xs" onClick={(e) => e.stopPropagation()}>
      <Switch checked={!!tpl.enabled} onCheckedChange={(v) => setTpl({ ...tpl, enabled: v })} data-testid="reply-enabled" />
      <span className="text-stone-500">Aktif</span>
    </label>
  );
  return (
    <CardCollapse
      testid="comm-template-reply"
      icon={<Mail size={16} />}
      title="İletişim formu otomatik cevap"
      description="Formu dolduran kişiye otomatik olarak gönderilecek teşekkür/onay e-postası. Aktifleştirmezseniz gönderilmez."
      chip={chip}
    >
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <Field label="Konu (subject)" hint="Değişkenler kullanılabilir.">
            <Input value={tpl.subject || ""} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} data-testid="reply-subject" />
          </Field>
          <Field label="Selamlama">
            <Input value={tpl.greeting || ""} onChange={(e) => setTpl({ ...tpl, greeting: e.target.value })} data-testid="reply-greeting" />
          </Field>
          <Field label="Mesaj metni">
            <Textarea rows={5} value={tpl.body || ""} onChange={(e) => setTpl({ ...tpl, body: e.target.value })} data-testid="reply-body" />
          </Field>
          <Field label="İmza">
            <Textarea rows={2} value={tpl.signature || ""} onChange={(e) => setTpl({ ...tpl, signature: e.target.value })} data-testid="reply-signature" />
          </Field>
          {PLACEHOLDERS}
          <div className="pt-3 mt-3 border-t border-stone-200">
            <ImageCropUpload
              label="Otomatik cevap fotoğrafı"
              hint="Otomatik yanıt e-postasının başlığında görünür. Boş bırakılırsa iletişim ayarlarındaki fotoğraf kullanılır."
              value={tpl.photo || ""}
              onChange={(url) => setTpl((p) => ({ ...p, photo: url }))}
              shape={tpl.photo_shape || "round"}
              onShapeChange={(sh) => setTpl((p) => ({ ...p, photo_shape: sh }))}
              alt="Otomatik cevap şablonu fotoğrafı"
              testid="reply-photo"
            />
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button type="button" onClick={save} disabled={busy} data-testid="reply-save">
              {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}Kaydet
            </Button>
            <Button type="button" variant="ghost" onClick={reset} data-testid="reply-reset">
              <RotateCcw size={13} className="mr-1" />Varsayılana döndür
            </Button>
          </div>
          {!tpl.enabled ? (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Şablon şu an pasif. Aktifleştirmek için sağ üstteki anahtarı açın; aksi hâlde form dolduran kişilere otomatik cevap gönderilmez.
            </p>
          ) : null}
        </div>
        <PreviewFrame html={preview.html} loading={preview.loading} subject={preview.subject} />
      </div>
    </CardCollapse>
  );
}

const APPOINTMENT_DEFAULTS = {
  subject: "{{host}} sizden randevu talep ediyor",
  greeting: "Merhaba {{name}},",
  intro: "{{host}} sizden bir randevu talep ediyor. Konu başlığını ve detayları aşağıda görebilirsiniz. Dilerseniz size uygun bir tarih önerebilir ya da aramamızı rica edebilirsiniz.",
  outro: "Yanıtınızı 7 gün içinde bekliyoruz. Teşekkür ederiz.",
  cta_create: "Randevu Oluştur",
  cta_call: "Beni Arayın!",
  callback_reminder_enabled: true,
  quiet_start: "00:00",
  quiet_end: "08:00",
  daily_summary_enabled: false,
  daily_summary_time: "08:00",
};

function AppointmentCard() {
  const { tpl, setTpl, preview, save, busy, reset } = useTemplate("appointment_template", APPOINTMENT_DEFAULTS, "appointment");
  const chip = <span className="text-[11px] text-stone-400">Randevu talebi</span>;
  return (
    <CardCollapse
      testid="comm-template-appointment"
      icon={<Users size={16} />}
      title="Randevu talep e-postası şablonu"
      description="Takvim sayfasından 'Randevu Talep Et' ile birine gönderdiğiniz davetin yazısı ve fotoğrafı. Alıcı bu e-postadaki butonlarla uygun tarihi önerir veya 'Beni Arayın' der."
      chip={chip}
    >
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <Field label="Konu (subject)" hint='Örn: "{{host}} sizden randevu talep ediyor"'>
            <Input value={tpl.subject || ""} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} data-testid="appt-tpl-subject" />
          </Field>
          <Field label="Selamlama">
            <Input value={tpl.greeting || ""} onChange={(e) => setTpl({ ...tpl, greeting: e.target.value })} data-testid="appt-tpl-greeting" />
          </Field>
          <Field label="Giriş metni" hint='"{{reason}}" ve "{{title}}" değişkenlerini kullanabilirsiniz.'>
            <Textarea rows={4} value={tpl.intro || ""} onChange={(e) => setTpl({ ...tpl, intro: e.target.value })} data-testid="appt-tpl-intro" />
          </Field>
          <Field label="Kapanış metni">
            <Textarea rows={2} value={tpl.outro || ""} onChange={(e) => setTpl({ ...tpl, outro: e.target.value })} data-testid="appt-tpl-outro" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label='"Randevu Oluştur" butonu'>
              <Input value={tpl.cta_create || ""} onChange={(e) => setTpl({ ...tpl, cta_create: e.target.value })} data-testid="appt-tpl-cta-create" />
            </Field>
            <Field label='"Beni Arayın" butonu'>
              <Input value={tpl.cta_call || ""} onChange={(e) => setTpl({ ...tpl, cta_call: e.target.value })} data-testid="appt-tpl-cta-call" />
            </Field>
          </div>
          <div className="text-[11px] text-stone-500 flex flex-wrap gap-x-3 gap-y-1 mt-1">
            <span><code className="bg-stone-100 px-1 rounded">{"{{name}}"}</code> alıcının adı</span>
            <span><code className="bg-stone-100 px-1 rounded">{"{{host}}"}</code> sizin adınız</span>
            <span><code className="bg-stone-100 px-1 rounded">{"{{title}}"}</code> talep başlığı</span>
            <span><code className="bg-stone-100 px-1 rounded">{"{{reason}}"}</code> talep nedeni</span>
            <span><code className="bg-stone-100 px-1 rounded">{"{{link}}"}</code> yanıt linki</span>
          </div>
          <div className="pt-3 mt-3 border-t border-stone-200">
            <ImageCropUpload
              label="Randevu e-postası fotoğrafı"
              hint="Randevu talebi e-postasının başında alıcıya görünür. Boş bırakılırsa iletişim ayarlarındaki fotoğraf kullanılır."
              value={tpl.photo || ""}
              onChange={(url) => setTpl((p) => ({ ...p, photo: url }))}
              shape={tpl.photo_shape || "round"}
              onShapeChange={(sh) => setTpl((p) => ({ ...p, photo_shape: sh }))}
              alt="Randevu şablonu fotoğrafı"
              testid="appt-tpl-photo"
            />
          </div>
          <div className="pt-3 mt-3 border-t border-stone-200">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-stone-900">Saatlik Telegram hatırlatıcı</div>
                <p className="text-xs text-stone-500 mt-0.5">
                  "Arama Bekliyor" durumundaki talepler için saatte bir Telegram ve push bildirimi gönderir. Sessiz saatler arasında gönderilmez.
                </p>
              </div>
              <Switch
                checked={tpl.callback_reminder_enabled !== false}
                onCheckedChange={(v) => setTpl({ ...tpl, callback_reminder_enabled: v })}
                data-testid="appt-tpl-callback-reminder"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Sessiz saat başlangıç" hint="Örn: 00:00">
                <Input type="time" value={tpl.quiet_start || "00:00"}
                  onChange={(e) => setTpl({ ...tpl, quiet_start: e.target.value })}
                  data-testid="appt-tpl-quiet-start" />
              </Field>
              <Field label="Sessiz saat bitiş" hint="Örn: 08:00">
                <Input type="time" value={tpl.quiet_end || "08:00"}
                  onChange={(e) => setTpl({ ...tpl, quiet_end: e.target.value })}
                  data-testid="appt-tpl-quiet-end" />
              </Field>
            </div>
          </div>
          <div className="pt-3 mt-3 border-t border-stone-200">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-stone-900">Günlük Telegram özeti</div>
                <p className="text-xs text-stone-500 mt-0.5">
                  Her sabah Telegram'a günün etkinliklerini, okunmamış mesajları ve arama bekliyor sayısını özetler.
                </p>
              </div>
              <Switch
                checked={!!tpl.daily_summary_enabled}
                onCheckedChange={(v) => setTpl({ ...tpl, daily_summary_enabled: v })}
                data-testid="appt-tpl-daily-summary"
              />
            </div>
            {tpl.daily_summary_enabled && (
              <div className="mt-3 max-w-[220px]">
                <Field label="Özet gönderim saati">
                  <Input type="time" value={tpl.daily_summary_time || "08:00"}
                    onChange={(e) => setTpl({ ...tpl, daily_summary_time: e.target.value })}
                    data-testid="appt-tpl-summary-time" />
                </Field>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button type="button" onClick={save} disabled={busy} data-testid="appt-tpl-save">
              {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}Kaydet
            </Button>
            <Button type="button" variant="ghost" onClick={reset} data-testid="appt-tpl-reset">
              <RotateCcw size={13} className="mr-1" />Varsayılana döndür
            </Button>
          </div>
        </div>
        <PreviewFrame html={preview.html} loading={preview.loading} subject={preview.subject} />
      </div>
    </CardCollapse>
  );
}

export default function CommTemplates() {
  return (
    <>
      <InviteCard />
      <AppointmentCard />
      <ReplyCard />
    </>
  );
}
